import React from 'react';
import {
  Box, Container, Stack, Skeleton,
  Grid,
} from '@mui/material';
import { ContextMenuComplete, TripMatchNotesCard } from '@marageti/z4-lib';
import { TripMatchStatus } from '@marageti/z4-sdk/lib/travel';
import { ViewProps } from './types';
import TripDetailCard, { getCanEditTripDetails } from '../../../components/trip-detail-card';
import ReassignForm from '../../../components/assign-agent-form';
import SpecialistCard from './specialist-card/specialist-card';
import Alerts from './alerts';
import TravelerCard from '../../../components/traveler-card';
import TripMatchSalesCard from '../../../components/trip-match-sales-card';
import SalesStageCard from './sales-stage-card';
import { shouldShowSalesCard } from '../../../utils/sales';
import { AlertType } from '../../../components/alerts/utils';

const DetailsView = ({
  agents,
  alert,
  assignedSpecialist,
  isAdmin,
  onClickEdit,
  showModal,
  toggleModal,
  travelers,
  tripMatch,
  tripMatchId,
  tripRequest,
  salesRecordings,
}: ViewProps) => {
  const canEditTripDetails = getCanEditTripDetails(tripMatch);

  // Canceled-trip banners sit at the top of the Trip Details column (right), next to the traveler
  // profile — not full-width above both columns like the other alerts.
  const isCanceledAlert = !!alert && (
    alert.type === AlertType.MatchSoldCanceled
    || alert.type === AlertType.MatchRefundedCanceled
    || alert.type === AlertType.MatchTravelerCanceled
  );

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          pt: 4,
          pb: 15,
        }}
      >
        <Box mb={1.5}>
          {(alert && alert.type !== AlertType.FirstMessageReminder && !isCanceledAlert) && <Alerts alert={alert} />}
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={5}>
            <Stack spacing={3}>
              {travelers ? (
                <TravelerCard
                  hidePhone={tripMatch?.status === TripMatchStatus.MatchPending || tripMatch?.status === TripMatchStatus.MatchAccepted}
                  travelers={travelers}
                  baseRoute={`/trip-matches/${tripMatchId}`}
                />
              ) : (<Skeleton variant="rounded" height={296} />)}
              <SpecialistCard specialist={assignedSpecialist} isAdmin={isAdmin} openModal={toggleModal} />
              <TripMatchNotesCard title="Notes from Zicasso" notes={{ ...tripRequest?.matchingNotes }} />
            </Stack>
          </Grid>
          <Grid item xs={12} lg={7}>
            <Stack spacing={3}>
              {isCanceledAlert && alert && <Alerts alert={alert} />}
              {
                (tripMatch?.status === TripMatchStatus.MatchAccepted || tripMatch?.status === TripMatchStatus.MatchActive) && (
                  <SalesStageCard
                    salesStage={tripMatch?.salesStage || ''}
                    reminder={
                      // @ts-ignore
                      tripMatch?.reminder
                      || {
                        dueAt: '',
                        index: 0,
                        startAt: '',
                        origDueAt: '',
                      }
                    }
                    travelerToPartnerId={tripMatch?.travelerToPartnerId || ''}
                    tripMatchId={tripMatchId || ''}
                    tripMatchCreatedAt={tripMatch.createdAt}
                  />
                )
              }
              <ReassignForm
                open={showModal}
                agents={agents}
                onClose={toggleModal}
                tripMatchId={tripMatchId || ''}
              />
              <TripDetailCard
                title="Trip Details"
                titleTypographyProps={{ variant: 'h3' }}
                tripMatch={tripMatch}
                tripRequest={tripRequest}
                action={canEditTripDetails && (
                  <ContextMenuComplete
                    id="edit-trip-match"
                    items={[{ action: onClickEdit, label: 'Edit' }]}
                  />
                )}
              />
              {tripMatch && shouldShowSalesCard(tripMatch) && <TripMatchSalesCard tripMatch={tripMatch} salesRecordings={salesRecordings} />}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};
export default DetailsView;
